import { yupResolver } from '@hookform/resolvers/yup'
import { find, map } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'
import { AlertCircle } from 'lucide-react'

import { ReferralType } from './ReferralTable'
import { getTranslation } from '../../../account/services/auth'
import Loader from '../../../common/components/Loader'
import { Currency } from '../../../common/models'
import { TicketCategory, TicketList } from '../../../common/models/ticket'
import FormGroup from '../../../core/components/FormGroup'
import {
  Referral,
  ReferralPayload,
  ReferralWarningDto,
  createReferral,
  deleteReferral,
  getReferral,
} from '../../../core/services/referrals'
import {
  getAllCategories,
  getTicketsByCategoryId,
} from '../../../core/services/tickets'
import translate from '../../../core/services/translate'
import { IUser, searchUsersByPhone } from '../../../core/services/users'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { PageTitle } from '@/components'
import { Button, Checkbox, Combobox, Input, Modal } from '@/components/ui'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import SelectField from '@/components/select-field'

export interface IReferralValues {
  appliesTo: string
  user?: string
  ticketCategory: string
  ticketId: string
  maxTicketsPerOrder: string
  cashbackRunaPercentageBuyer?: string
  cashbackRunaAmountBuyer?: string
  cashbackAvailableUntilBuyer?: string
  cashbackAvailableNumberOfDaysBuyer?: string
  cashbackRunaPercentageReferrer?: string
  cashbackRunaAmountReferrer?: string
  cashbackAvailableUntilReferrer?: string
  cashbackAvailableNumberOfDaysReferrer?: string
  discountPercentageBuyer?: string
  discountAmountBuyer?: string
  maxRewardsInRunaBuyer?: string
  unlimited?: boolean | null
  startDate?: string
  endDate?: string
  addToBag?: boolean | null
  published?: boolean | null
}

const appliesTypes = [
  { value: 'all', label: 'All users' },
  { value: 'single', label: 'Single user' },
]

export interface AddEditModalProps {
  visible?: boolean
  handleCloseModal?: () => void
  item?: Referral
  type: ReferralType
  currency: Currency | null
  onClose?: () => void
}

export const AddEditModal = (props: AddEditModalProps): JSX.Element => {
  const { visible, handleCloseModal, item, type, currency, onClose } = props
  const [referralWarning, setReferralWarning] = useState<ReferralWarningDto>()
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isUsersLoading, setIsUsersLoading] = useState<boolean>(false)
  const language = getTranslation()
  const [users, setUsers] = useState<IUser[]>([])
  const [selectedUser, setSelectedUser] = useState<IUser>()

  const [tickets, setTickets] = useState<TicketList[]>([])
  const [ticketCategoriesData, setTicketCategoriesData] = useState<
    TicketCategory[]
  >([])
  const [submitting, setSubmitting] = useState<boolean>(false)
  const { editionId } = useParams()

  const schemaObject = {
    appliesTo: yup.string().required('Applies to is required'),
    user: yup.string().when('appliesTo', {
      is: (val: string) => val === 'single',
      then: (schema) => schema.required('phone is required'),
      otherwise: (schema) => schema.optional(),
    }),
    ticketCategory: yup.string().required('Ticket Category is required'),
    ticketId: yup.string().required('Ticket ID is required'),
    maxTicketsPerOrder: yup
      .string()
      .required('Max Tickets Per Order is required'),
    cashbackRunaPercentageBuyer: yup.string().optional(),
    cashbackRunaAmountBuyer: yup.string().optional(),
    cashbackAvailableUntilBuyer: yup.string().optional(),
    cashbackAvailableNumberOfDaysBuyer: yup.string().optional(),
    cashbackRunaPercentageReferrer: yup.string().optional(),
    cashbackRunaAmountReferrer: yup.string().optional(),
    cashbackAvailableUntilReferrer: yup.string().optional(),
    cashbackAvailableNumberOfDaysReferrer: yup.string().optional(),
    discountPercentageBuyer: yup.string().optional(),
    discountAmountBuyer: yup.string().optional(),
    maxRewardsInRunaBuyer: yup.string().optional(),
    unlimited: yup.boolean().optional().nullable(),
    startDate: yup.string().optional(),
    endDate: yup.string().optional(),
    addToBag: yup.boolean().optional().nullable(),
    published: yup.boolean().optional().nullable(),
  }

  const schema = yup.object().shape(schemaObject)

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<IReferralValues>({
    mode: 'onChange',
    resolver: yupResolver<IReferralValues>(schema),
  })

  const getData = useCallback(async () => {
    if (!editionId) return

    if (item?.id) {
      setIsLoading(true)
      try {
        const [referralData, categoriesData] = await Promise.all([
          getReferral(item.id),
          getAllCategories({ size: 1000 }),
        ])
        if (referralData.status === 200) {
          setValue('ticketCategory', referralData.data.ticketCategoryId)
          if (referralData.data.userProfile) {
            setValue('user', referralData.data.userProfile.id, {
              shouldValidate: true,
            })
            setSelectedUser(referralData.data.userProfile)
          }
          getCategoryTickets(referralData.data.ticketCategoryId)
          setValue('ticketId', referralData.data.ticketId)
          setValue('addToBag', referralData.data.addToBag)
          if (referralData.data.endDate) {
            setValue(
              'endDate',
              new Date(referralData.data.endDate).toISOString().split('T')[0],
            )
          }
          if (referralData.data.startDate) {
            setValue(
              'startDate',
              new Date(referralData.data.startDate).toISOString().split('T')[0],
            )
          }
          if (referralData.data.maxPerOrder) {
            setValue(
              'maxTicketsPerOrder',
              referralData.data.maxPerOrder.toString(),
            )
          }
          setValue('published', referralData.data.published)
          if (referralData.data.maxReward) {
            setValue(
              'maxRewardsInRunaBuyer',
              referralData.data.maxReward.toString(),
            )
          }
          if (referralData.data.buyerCashbackPercentage) {
            setValue(
              'cashbackRunaPercentageBuyer',
              referralData.data.buyerCashbackPercentage.toString(),
            )
          }
          if (referralData.data.buyerCashbackValue) {
            setValue(
              'cashbackRunaAmountBuyer',
              referralData.data.buyerCashbackValue.toString(),
            )
          }
          if (referralData.data.buyerCashbackAvailableDays) {
            setValue(
              'cashbackAvailableNumberOfDaysBuyer',
              referralData.data.buyerCashbackAvailableDays.toString(),
            )
          }
          if (referralData.data.buyerCashbackExpiryDate) {
            setValue(
              'cashbackAvailableUntilBuyer',
              new Date(referralData.data.buyerCashbackExpiryDate)
                .toISOString()
                .split('T')[0],
            )
          }
          if (referralData.data.referrerCashbackPercentage) {
            setValue(
              'cashbackRunaPercentageReferrer',
              referralData.data.referrerCashbackPercentage.toString(),
            )
          }
          if (referralData.data.referrerCashbackValue) {
            setValue(
              'cashbackRunaAmountReferrer',
              referralData.data.referrerCashbackValue.toString(),
            )
          }
          if (referralData.data.referrerCashbackExpiryDate) {
            setValue(
              'cashbackAvailableUntilReferrer',
              new Date(referralData.data.referrerCashbackExpiryDate)
                .toISOString()
                .split('T')[0],
            )
          }
          if (referralData.data.referrerCashbackAvailableDays) {
            setValue(
              'cashbackAvailableNumberOfDaysReferrer',
              referralData.data.referrerCashbackAvailableDays.toString(),
            )
          }
          if (referralData.data.referralDiscountPercentage) {
            setValue(
              'discountPercentageBuyer',
              referralData.data.referralDiscountPercentage.toString(),
            )
          }
          if (referralData.data.referralDiscountValue) {
            setValue(
              'discountAmountBuyer',
              referralData.data.referralDiscountValue.toString(),
            )
          }
        }
        if (categoriesData.status === 200) {
          setTicketCategoriesData(categoriesData.data.content)
        }
      } catch (err) {
        console.log(err)
      } finally {
        setIsLoading(false)
      }
    } else {
      try {
        setIsLoading(true)
        const { data } = await getAllCategories({ size: 1000 })
        setTicketCategoriesData(data.content)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setIsLoading(false)
      }
    }
  }, [editionId, item, visible, setValue])

  useEffect(() => {
    if (visible) {
      getData()
    }
  }, [visible, getData])

  const handleSearch = async (query: string, signal: AbortSignal) => {
    setIsUsersLoading(true)
    try {
      const { data, status } = await searchUsersByPhone(query, signal)
      if (status === 200) {
        setUsers(data)
        setIsUsersLoading(false)
      }
    } catch (e) {
      if (signal.aborted) return
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setIsUsersLoading(false)
    }
  }

  const getCategoryTickets = async (category?: string) => {
    try {
      if (category) {
        const { data } = await getTicketsByCategoryId(
          category,
          { size: 1000 },
          false,
        )
        setTickets(data.content)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  useEffect(() => {
    setValue('appliesTo', type)
  }, [type])

  const handleClose = () => {
    handleCloseModal?.()
    reset()
    setSelectedUser(undefined)
    setTicketCategoriesData([])
    setTickets([])
  }

  const submit = async (values: IReferralValues) => {
    try {
      const payload: ReferralPayload = {
        maxPerOrder: values.maxTicketsPerOrder,
        unlimited: values.unlimited ? true : false,
        published: values.published ? true : false,
        addToBag: values.addToBag ? true : false,
        ticketId: values.ticketId,
        maxReward: values.maxRewardsInRunaBuyer
          ? values.maxRewardsInRunaBuyer
          : undefined,
        referrerCashbackValue: values.cashbackRunaAmountReferrer,
        buyerCashbackExpiryDate: values.cashbackAvailableUntilBuyer
          ? new Date(values.cashbackAvailableUntilBuyer).valueOf()
          : undefined,
        buyerCashbackAvailableDays: values.cashbackAvailableNumberOfDaysBuyer,
        buyerCashbackPercentage: values.cashbackRunaPercentageBuyer,
        buyerCashbackValue: values.cashbackRunaAmountBuyer,
        referralDiscountPercentage: values.discountPercentageBuyer,
        referralDiscountValue: values.discountAmountBuyer,
        referrerCashbackAvailableDays:
          values.cashbackAvailableNumberOfDaysReferrer,
        referrerCashbackExpiryDate: values.cashbackAvailableUntilReferrer
          ? new Date(values.cashbackAvailableUntilReferrer).valueOf()
          : undefined,
        referrerCashbackPercentage: values.cashbackRunaPercentageReferrer,
        startDate: values.startDate
          ? new Date(values.startDate).valueOf()
          : undefined,
        endDate: values.endDate
          ? new Date(values.endDate).valueOf()
          : undefined,
      }
      if (values.user) {
        payload.userProfileId = values.user
      }

      if (item?.id) {
        payload.id = item.id
      }

      setSubmitting(true)

      const { status, data } = await createReferral(payload)
      if (status === 200) {
        if (data.referralWarningDto) {
          setReferralWarning(data.referralWarningDto)
        } else {
          handleClose()
          onClose?.()
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const editMode = !!item

  const generateClassBasedOnPublish = (ticket: string) => {
    if (!ticket) return undefined
    const t = find(tickets, (tic) => tic.id === ticket)
    if (!t) return undefined

    if (t.published) {
      return 'select-green'
    } else {
      if (t.publishTimestamp - new Date().valueOf() <= 0) {
        return 'select-red'
      } else {
        return 'select-yellow'
      }
    }
  }

  const ticketCategory = watch('ticketCategory')

  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState(false)

  const handleDelete = () => {
    setDeleteConfirmationModal(true)
  }

  const deleteFunc = async () => {
    if (!item) return
    try {
      const { status } = await deleteReferral(item.id)
      if (status === 204) {
        handleCloseModal?.()
        handleClose()
        onClose?.()
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  return (
    <>
      <Modal
        open={!!visible}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="gift"
              title={translate(
                language.value,
                item?.id
                  ? 'referral.updateReferral'
                  : 'referral.addNewReferral',
              )}
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {editMode ? (
              <Button
                onClick={handleDelete}
                variant="secondary"
                className="mr-auto bg-accent-3 text-white hover:bg-accent-3/90"
              >
                {translate(language.value, 'common.delete')}
              </Button>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                {translate(language.value, 'common.cancel')}
              </Button>
              <Button
                type="submit"
                form="referral-form"
                variant="primary"
                loading={submitting}
              >
                {submitting
                  ? translate(language.value, 'common.submitting')
                  : translate(
                      language.value,
                      editMode ? 'common.update' : 'common.save',
                    )}
              </Button>
            </div>
          </div>
        }
      >
        {(submitting || isLoading) && <Loader />}
        <form
          id="referral-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label={translate(language.value, 'referral.appliesTo')}>
            <Input
              isInvalid={!!errors?.appliesTo?.message}
              {...register('appliesTo')}
              type="text"
              readOnly
              value={find(appliesTypes, { value: type })?.label}
            />
          </FormGroup>
          {type === 'single' && (
            <FormGroup
              label={translate(language.value, 'common.searchUser')}
              wrapperClassName="w-full"
            >
              <Controller
                control={control}
                name="user"
                render={({ field }) => (
                  <Combobox
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value)
                      const user = users.find((u) => u.id === value)
                      setSelectedUser(user)
                    }}
                    onSearch={handleSearch}
                    isLoading={isUsersLoading}
                    minSearchLength={3}
                    placeholder={translate(language.value, 'common.searchUser')}
                    searchPlaceholder={translate(
                      language.value,
                      'common.searchUser',
                    )}
                    isInvalid={!!errors?.user?.message}
                    options={users.map((user) => ({
                      label: user.phone || '',
                      value: user.id || '',
                    }))}
                    renderOption={(option) => {
                      const user = users.find((u) => u.id === option.value)
                      if (!user) return option.label
                      return (
                        <div>
                          <span>
                            {user.phone}{' '}
                            <span className="text-monochrome-120">
                              ({user.firstName} {user.lastName})
                            </span>
                          </span>
                        </div>
                      )
                    }}
                  />
                )}
              />
            </FormGroup>
          )}
          {selectedUser && (
            <div className="mt-2 p-3 rounded-lg bg-monochrome-5-50 border border-monochrome-120">
              <p className="mb-0">{`${selectedUser.firstName} ${selectedUser.lastName}`}</p>
              <p className="mb-0">{selectedUser.phone}</p>
              <span>{selectedUser.email}</span>
              <div>
                <span className="font-normal">
                  {translate(language.value, 'referral.referralCode')}:{' '}
                  <span className="font-bold">{selectedUser.referralCode}</span>
                </span>
              </div>
            </div>
          )}

          <div className="flex items-end gap-3 w-full">
            <FormGroup
              label={translate(language.value, 'referral.selectTicketCategory')}
              tooltip={translate(
                language.value,
                'rewards.vouchers.appliedForTicketsTooltip',
              )}
              wrapperClassName="flex-1"
            >
              <Controller
                name="ticketCategory"
                control={control}
                render={({ field }) => (
                  <SelectField
                    {...field}
                    outline
                    error={errors?.ticketCategory?.message}
                    placeholder={translate(
                      language.value,
                      'referral.selectTicketCategory',
                    )}
                    options={ticketCategoriesData.map((tick) => ({
                      label: tick.name,
                      value: tick.id,
                    }))}
                    onValueChange={(value) => {
                      field.onChange(value)
                      getCategoryTickets(value)
                    }}
                  />
                )}
              />
            </FormGroup>

            <FormGroup
              label={translate(language.value, 'referral.selectTicket')}
              tooltip={translate(
                language.value,
                'rewards.vouchers.selectTicketsTooltip',
              )}
              wrapperClassName="flex-1"
            >
              <Controller
                name="ticketId"
                control={control}
                render={({ field }) => (
                  <SelectField
                    {...field}
                    outline
                    error={errors?.ticketId?.message}
                    placeholder={translate(
                      language.value,
                      'referral.selectTicket',
                    )}
                    options={tickets.map((tick) => ({
                      label: `${tick.name} - ${tick.price} ${currency?.symbol}`,
                      value: tick.id,
                    }))}
                    onValueChange={field.onChange}
                  />
                )}
              />
            </FormGroup>
          </div>

          <FormGroup
            label={translate(language.value, 'referral.maxTicketsPerOrder')}
          >
            <Input
              isInvalid={!!errors?.maxTicketsPerOrder?.message}
              {...register('maxTicketsPerOrder')}
              type="text"
              placeholder={translate(language.value, 'common.enterAmount')}
            />
          </FormGroup>
          <div className="flex items-center gap-2">
            <FormGroup
              label={translate(
                language.value,
                'referral.cashbackPercentageInRUNAForBuyer',
              )}
              tooltip={translate(
                language.value,
                'referral.cashbackPercentageInRUNAForBuyer',
              )}
              wrapperClassName="flex-1"
            >
              <Input
                isInvalid={!!errors?.cashbackRunaPercentageBuyer?.message}
                {...register('cashbackRunaPercentageBuyer')}
                type="text"
                placeholder={translate(
                  language.value,
                  'common.enterPercentage',
                )}
              />
            </FormGroup>
            <span className="mt-4 text-monochrome-1">
              {translate(language.value, 'common.or')}
            </span>
            <FormGroup
              label={translate(
                language.value,
                'referral.cashbackAmountInRUNAForBuyer',
              )}
              tooltip={translate(
                language.value,
                'referral.cashbackAmountInRUNAForBuyer',
              )}
              wrapperClassName="flex-1"
            >
              <Input
                isInvalid={!!errors?.cashbackRunaAmountBuyer?.message}
                {...register('cashbackRunaAmountBuyer')}
                type="text"
                placeholder={translate(language.value, 'common.enterAmount')}
              />
            </FormGroup>
          </div>

          <div className="flex items-center gap-2">
            <FormGroup
              label={translate(
                language.value,
                'referral.cashbackAvailableUntil',
              )}
              wrapperClassName="flex-1"
            >
              <Controller
                name="cashbackAvailableUntilBuyer"
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    mode="date"
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) =>
                      field.onChange(
                        date ? date.toISOString().split('T')[0] : '',
                      )
                    }
                    hasError={!!errors?.cashbackAvailableUntilBuyer?.message}
                  />
                )}
              />
            </FormGroup>
            <span className="mt-4 text-monochrome-1">
              {translate(language.value, 'common.or')}
            </span>
            <FormGroup label="&nbsp;" wrapperClassName="flex-1">
              <Input
                isInvalid={
                  !!errors?.cashbackAvailableNumberOfDaysBuyer?.message
                }
                {...register('cashbackAvailableNumberOfDaysBuyer')}
                type="text"
                placeholder={translate(language.value, 'common.numberOfDays')}
              />
            </FormGroup>
          </div>

          <div className="my-4 h-px w-full bg-monochrome-120" />

          <div className="flex items-center gap-2">
            <FormGroup
              label={translate(
                language.value,
                'referral.cashbackPercentageInRUNAForReferrer',
              )}
              tooltip={translate(
                language.value,
                'referral.cashbackPercentageInRUNAForReferrer',
              )}
              wrapperClassName="flex-1"
            >
              <Input
                isInvalid={!!errors?.cashbackRunaPercentageReferrer?.message}
                {...register('cashbackRunaPercentageReferrer')}
                type="text"
                placeholder={translate(
                  language.value,
                  'common.enterPercentage',
                )}
              />
            </FormGroup>
            <span className="mt-4 text-monochrome-1">
              {translate(language.value, 'common.or')}
            </span>
            <FormGroup
              label={translate(
                language.value,
                'referral.cashbackAmountInRUNAForReferrer',
              )}
              tooltip={translate(
                language.value,
                'referral.cashbackAmountInRUNAForReferrer',
              )}
              wrapperClassName="flex-1"
            >
              <Input
                isInvalid={!!errors?.cashbackRunaAmountReferrer?.message}
                {...register('cashbackRunaAmountReferrer')}
                type="text"
                placeholder={translate(language.value, 'common.enterAmount')}
              />
            </FormGroup>
          </div>

          <div className="flex items-center gap-2">
            <FormGroup
              label={translate(
                language.value,
                'referral.cashbackAvailableUntil',
              )}
              wrapperClassName="flex-1"
            >
              <Controller
                name="cashbackAvailableUntilReferrer"
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    mode="date"
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) =>
                      field.onChange(
                        date ? date.toISOString().split('T')[0] : '',
                      )
                    }
                    hasError={!!errors?.cashbackAvailableUntilReferrer?.message}
                  />
                )}
              />
            </FormGroup>
            <span className="mt-4 text-monochrome-1">
              {translate(language.value, 'common.or')}
            </span>
            <FormGroup label="&nbsp;" wrapperClassName="flex-1">
              <Input
                isInvalid={
                  !!errors?.cashbackAvailableNumberOfDaysReferrer?.message
                }
                {...register('cashbackAvailableNumberOfDaysReferrer')}
                type="text"
                placeholder={translate(language.value, 'common.numberOfDays')}
              />
            </FormGroup>
          </div>

          <div className="flex items-center gap-2">
            <FormGroup
              label={translate(
                language.value,
                'referral.discountTicketInPercentageFromPriceForBuyer',
              )}
              tooltip={translate(
                language.value,
                'referral.discountTicketInPercentageFromPriceForBuyer',
              )}
              wrapperClassName="flex-1"
            >
              <Input
                isInvalid={!!errors?.discountPercentageBuyer?.message}
                {...register('discountPercentageBuyer')}
                type="text"
                placeholder={translate(
                  language.value,
                  'common.enterPercentage',
                )}
              />
            </FormGroup>
            <span className="mt-4 text-monochrome-1">
              {translate(language.value, 'common.or')}
            </span>
            <FormGroup
              label={translate(
                language.value,
                'referral.discountAmountInRONFromPriceForBuyer',
              )}
              tooltip={translate(
                language.value,
                'referral.discountAmountInRONFromPriceForBuyer',
              )}
              wrapperClassName="flex-1"
            >
              <Input
                isInvalid={!!errors?.discountAmountBuyer?.message}
                {...register('discountAmountBuyer')}
                type="text"
                placeholder={translate(language.value, 'common.enterAmount')}
              />
            </FormGroup>
          </div>

          <FormGroup
            label={translate(language.value, 'referral.maxRewardInRUNA')}
          >
            <Input
              isInvalid={!!errors?.maxRewardsInRunaBuyer?.message}
              {...register('maxRewardsInRunaBuyer')}
              type="text"
              placeholder={translate(language.value, 'common.enterAmount')}
            />
          </FormGroup>

          <FormGroup
            wrapperClassName="w-auto gap-4"
            direction="row"
            label={translate(language.value, 'common.unlimited')}
          >
            <Controller
              name="unlimited"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>

          <div className="flex items-center gap-2">
            <FormGroup
              label={translate(language.value, 'common.startDate')}
              tooltip={translate(language.value, 'common.startDate')}
              wrapperClassName="flex-1"
            >
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    mode="date"
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) =>
                      field.onChange(
                        date ? date.toISOString().split('T')[0] : '',
                      )
                    }
                    placeholder={translate(language.value, 'common.selectDate')}
                    hasError={!!errors?.startDate?.message}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label={translate(language.value, 'common.endDate')}
              tooltip={translate(language.value, 'common.endDate')}
              wrapperClassName="flex-1"
            >
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    mode="date"
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) =>
                      field.onChange(
                        date ? date.toISOString().split('T')[0] : '',
                      )
                    }
                    placeholder={translate(language.value, 'common.selectDate')}
                    hasError={!!errors?.endDate?.message}
                  />
                )}
              />
            </FormGroup>
          </div>

          <FormGroup
            wrapperClassName="w-auto gap-4"
            direction="row"
            labelClassName="text-nowrap"
            label={translate(language.value, 'common.addToBag')}
          >
            <Controller
              name="addToBag"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>

          <FormGroup
            wrapperClassName="w-auto gap-4"
            direction="row"
            label={translate(language.value, 'common.published')}
          >
            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>

          {!!referralWarning && (
            <div className="mt-4 p-4 rounded-lg bg-yellow-50 border border-yellow-200">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="mb-2">
                    {translate(
                      language.value,
                      'referral.referralWarningTicket',
                    )}
                  </p>
                  {!!referralWarning?.userSummaryDto && (
                    <>
                      <p className="mb-0 font-bold">
                        {referralWarning?.userSummaryDto?.firstName}{' '}
                        {referralWarning?.userSummaryDto?.lastName}
                      </p>
                      <p className="mb-0 font-bold">
                        {referralWarning?.userSummaryDto?.phone}
                      </p>
                      <p className="mb-0 font-bold">
                        {referralWarning?.userSummaryDto?.email}
                      </p>
                      <p className="mb-0 font-bold">
                        {translate(language.value, 'referral.referralCode')}:{' '}
                        {referralWarning?.userSummaryDto?.referralCode}
                      </p>
                    </>
                  )}
                  <p className="mb-0 font-bold">
                    {referralWarning?.ticketName}
                  </p>
                </div>
              </div>
            </div>
          )}
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={translate(language.value, 'referral.title')}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
